import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Plus, Link2, Copy, Power, FileText } from "lucide-react";
import { API } from "../lib/api";

const api = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: "include", ...opts }).then(async (r) => {
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Errore"); }
    return r.json();
  });

export default function AdminProducts({ onCount }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", subtitle: "", description: "", amount: "", regular: "", launch: false });

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api("/admin/products"); setProducts(d.items || []); onCount?.(d.items?.length || 0); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) return;
    setCreating(true);
    try {
      await api("/admin/products", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, subtitle: form.subtitle, description: form.description,
          amount: Math.round(parseFloat(form.amount) * 100), currency: "eur",
          regular: form.regular || null, launch: form.launch,
        }),
      });
      toast.success("Prodotto creato (stato: bozza)");
      setForm({ name: "", subtitle: "", description: "", amount: "", regular: "", launch: false });
      load();
    } catch (e) { toast.error(e.message); }
    setCreating(false);
  };

  return (
    <div data-testid="admin-products">
      {/* create */}
      <form onSubmit={create} className="bg-white border border-[var(--line)] p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="product-create-form">
        <div className="sm:col-span-2 font-serif text-xl text-[var(--ink)]">Nuovo prodotto digitale</div>
        <input data-testid="np-name" required placeholder="Nome*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        <input data-testid="np-subtitle" placeholder="Sottotitolo (es. Toolkit + Framework)" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        <textarea data-testid="np-desc" placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="sm:col-span-2 border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)] resize-none" />
        <input data-testid="np-amount" required type="number" step="0.01" placeholder="Prezzo € (es. 149)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        <input data-testid="np-regular" placeholder="Prezzo pieno (facolt., es. €390)" value={form.regular} onChange={(e) => setForm({ ...form, regular: e.target.value })} className="border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        <label className="flex items-center gap-2 text-sm text-[var(--ink-soft)]">
          <input type="checkbox" checked={form.launch} onChange={(e) => setForm({ ...form, launch: e.target.checked })} /> Launch Edition
        </label>
        <div className="sm:col-span-2">
          <button data-testid="np-submit" disabled={creating} className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3 disabled:opacity-60">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} /> Crea prodotto</>}
          </button>
        </div>
      </form>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div>
        : <div className="space-y-6">{products.map((p) => <ProductCard key={p.id} p={p} reload={load} />)}</div>}
    </div>
  );
}

function ProductCard({ p, reload }) {
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(p.version);

  const patch = async (body) => {
    setBusy(true);
    try { await api(`/admin/products/${p.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api(`/admin/products/${p.id}/files`, { method: "POST", body: fd }); toast.success("File caricato"); reload(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); e.target.value = ""; }
  };

  const delFile = async (fid) => { setBusy(true); try { await api(`/admin/products/${p.id}/files/${fid}`, { method: "DELETE" }); reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };

  const delProduct = async () => { if (!window.confirm(`Eliminare "${p.name}"?`)) return; try { await api(`/admin/products/${p.id}`, { method: "DELETE" }); toast.success("Prodotto eliminato"); reload(); } catch (e) { toast.error(e.message); } };

  const regen = async () => {
    const email = window.prompt("Email del cliente (facoltativa, per invio automatico):", "");
    if (email === null) return;
    setBusy(true);
    try {
      const d = await api(`/admin/products/${p.id}/regenerate-link`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email || null, origin_url: window.location.origin, send_email: !!email }),
      });
      await navigator.clipboard?.writeText(d.link).catch(() => {});
      toast.success(email ? "Link generato e inviato via email (copiato)" : "Link generato e copiato negli appunti");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[var(--line)] p-6" data-testid={`product-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-serif text-2xl text-[var(--ink)]">{p.name}</h3>
            <span className={`text-[0.65rem] uppercase tracking-[0.15em] px-2 py-0.5 ${p.status === "active" ? "bg-[var(--petrol)] text-white" : "bg-[var(--line)] text-[var(--ink-soft)]"}`}>{p.status}</span>
            {!p.download_enabled && <span className="text-[0.65rem] uppercase tracking-[0.15em] px-2 py-0.5 bg-red-100 text-red-700">download off</span>}
          </div>
          <div className="text-sm text-[var(--ink-soft)] mt-1">{p.subtitle} · {p.price}{p.regular ? ` (pieno ${p.regular})` : ""} · v{p.version} · <span className="font-mono text-xs">{p.lookup_key}</span></div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`toggle-status-${p.id}`} disabled={busy} onClick={() => patch({ status: p.status === "active" ? "draft" : "active" })} className="text-xs uppercase tracking-[0.12em] border border-[var(--line)] px-3 py-2 hover:border-[var(--petrol)]">{p.status === "active" ? "Metti in bozza" : "Pubblica"}</button>
          <button data-testid={`toggle-dl-${p.id}`} disabled={busy} onClick={() => patch({ download_enabled: !p.download_enabled })} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.12em] border border-[var(--line)] px-3 py-2 hover:border-[var(--petrol)]"><Power size={13} /> {p.download_enabled ? "Disabilita DL" : "Abilita DL"}</button>
          <button disabled={busy} onClick={delProduct} className="text-[var(--ink-soft)] hover:text-red-600 p-2"><Trash2 size={16} /></button>
        </div>
      </div>

      {/* files */}
      <div className="mt-5 border-t border-[var(--line)] pt-4">
        <div className="flex items-center justify-between mb-3">
          <span className="overline !text-[var(--anthracite)]">File associati ({p.files.length})</span>
          <div className="flex items-center gap-3">
            <label className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold border border-[var(--petrol)] text-[var(--petrol)] hover:text-white px-4 py-2 cursor-pointer">
              <Upload size={14} /> Carica file
              <input data-testid={`upload-${p.id}`} type="file" className="hidden" onChange={upload} />
            </label>
            <button data-testid={`regen-${p.id}`} disabled={busy || !p.files.length} onClick={regen} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold text-[var(--accent)] hover:text-[var(--petrol)] disabled:opacity-40"><Link2 size={14} /> Rigenera link</button>
          </div>
        </div>
        {p.files.length === 0 ? <p className="text-sm text-[var(--ink-soft)]">Nessun file. Carica almeno un file per consegnare il prodotto.</p>
          : <ul className="space-y-2">
              {p.files.map((f) => (
                <li key={f.file_id} className="flex items-center justify-between bg-[var(--paper-2)] px-4 py-2 text-sm">
                  <span className="flex items-center gap-2 text-[var(--ink)]"><FileText size={15} className="text-[var(--gold)]" /> {f.filename} <span className="text-[var(--ink-soft)] text-xs">({Math.max(1, Math.round((f.size || 0) / 1024))} KB)</span></span>
                  <button disabled={busy} onClick={() => delFile(f.file_id)} className="text-[var(--ink-soft)] hover:text-red-600"><Trash2 size={15} /></button>
                </li>
              ))}
            </ul>}
      </div>

      {/* version */}
      <div className="mt-4 flex items-center gap-3">
        <span className="text-xs text-[var(--ink-soft)] uppercase tracking-[0.12em]">Versione</span>
        <input data-testid={`version-${p.id}`} value={version} onChange={(e) => setVersion(e.target.value)} className="w-24 border border-[var(--line)] px-2 py-1 text-sm outline-none focus:border-[var(--petrol)]" />
        <button disabled={busy || version === p.version} onClick={() => patch({ version })} className="text-xs uppercase tracking-[0.12em] text-[var(--petrol)] link-underline disabled:opacity-40">Salva</button>
      </div>
    </div>
  );
}
